import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import Breadcrumb from "@/components/feature/Breadcrumb";
import SectionHeading from "@/components/feature/SectionHeading";
import MovieCard from "@/components/feature/MovieCard";
import EmptyState from "@/components/base/EmptyState";
import { usePageMeta } from "@/hooks/usePageMeta";
import { useFavorites } from "@/hooks/useFavorites";
import { useHistory } from "@/hooks/useHistory";
import {
  getMediaById,
  getNewsByMediaId,
  getRelatedMedia,
  formatHeat,
} from "@/utils/media";
import { typeLabels } from "@/utils/labels";

export default function DetailPage() {
  const { id = "" } = useParams();
  const media = getMediaById(id);

  const { isFavorite, toggleFavorite } = useFavorites();
  const { addHistory } = useHistory();
  const [toast, setToast] = useState("");

  const favorited = media ? isFavorite(media.id) : false;

  usePageMeta({
    title: media
      ? `${media.title} - 影视详情 - 17C.COM`
      : "影视详情 - 17C.COM",
    description: media
      ? `${media.title}（${media.year}，${media.region}，${media.genres.join("/")}）豆瓣评分${media.rating}。${media.synopsis.slice(0, 50)}... 17C.COM 提供影片简介、演职人员与相关资讯。`
      : "该影视内容已下架或不存在。",
    keywords: media
      ? `${media.title},${media.genres.join(",")},${media.region}影视,影视详情,17C.COM`
      : "影视详情,17C.COM",
  });

  useEffect(() => {
    if (media) addHistory(media.id);
  }, [media, addHistory]);

  useEffect(() => {
    if (!toast) return;
    const timer = window.setTimeout(() => setToast(""), 2200);
    return () => window.clearTimeout(timer);
  }, [toast]);

  if (!media) {
    return (
      <div className="mx-auto w-full max-w-7xl px-4 py-16 md:px-6">
        <EmptyState
          icon="ri-emotion-sad-line"
          title="该影视内容已下架或不存在"
          description="你访问的影视作品可能已下架，或链接有误。"
          action={
            <div className="flex flex-wrap justify-center gap-3">
              <Link
                to="/category"
                className="flex items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-600 px-5 py-2.5 text-sm font-bold text-background-50 transition-colors hover:bg-primary-700"
              >
                <i className="ri-play-list-line" aria-hidden="true" />
                浏览影视分类
              </Link>
              <Link
                to="/"
                className="flex items-center gap-1.5 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-5 py-2.5 text-sm font-medium text-foreground-700 transition-colors hover:bg-background-100"
              >
                返回首页
              </Link>
            </div>
          }
        />
      </div>
    );
  }

  const relatedNews = getNewsByMediaId(media.id);
  const related = getRelatedMedia(media, 6);

  const handleShare = async () => {
    const url = window.location.href;
    try {
      await navigator.clipboard.writeText(url);
      setToast("链接已复制到剪贴板");
    } catch {
      setToast(`分享链接：${url}`);
    }
  };

  const handleFavorite = () => {
    const next = toggleFavorite(media.id);
    setToast(next ? "已加入我的收藏" : "已取消收藏");
  };

  return (
    <div className="w-full">
      {/* Hero */}
      <section className="relative w-full overflow-hidden bg-primary-950">
        <div className="absolute inset-0">
          <img
            src={media.cover}
            alt=""
            aria-hidden="true"
            className="h-full w-full object-cover object-top opacity-40 blur-sm"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-primary-950 via-primary-950/85 to-primary-950/70" />
        </div>

        <div className="relative mx-auto w-full max-w-7xl px-4 py-8 md:px-6 md:py-12">
          <div className="mb-6">
            <ol className="flex flex-wrap items-center gap-1.5 text-sm text-background-300">
              <li className="flex items-center gap-1.5">
                <Link to="/" className="hover:text-accent-400">首页</Link>
                <i className="ri-arrow-right-s-line" aria-hidden="true" />
              </li>
              <li className="flex items-center gap-1.5">
                <Link to={`/category/${media.type}`} className="hover:text-accent-400">
                  {typeLabels[media.type]}
                </Link>
                <i className="ri-arrow-right-s-line" aria-hidden="true" />
              </li>
              <li className="text-background-100">{media.title}</li>
            </ol>
          </div>

          <div className="flex flex-col gap-6 md:flex-row md:gap-8">
            <div className="w-36 shrink-0 md:w-56">
              <div className="aspect-[3/4] w-full overflow-hidden rounded-lg bg-primary-800">
                <img
                  src={media.cover}
                  alt={`${media.title} 海报`}
                  title={media.title}
                  className="h-full w-full object-cover object-top"
                />
              </div>
            </div>

            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-2">
                <span className="rounded bg-accent-500 px-2 py-0.5 text-xs font-bold text-background-50">
                  {typeLabels[media.type]}
                </span>
                <span className="rounded bg-background-50/15 px-2 py-0.5 text-xs font-medium text-background-100">
                  {media.episodes > 1 ? `全 ${media.episodes} 集` : "电影"}
                </span>
                <span className="rounded bg-background-50/15 px-2 py-0.5 text-xs font-medium text-background-100">
                  {media.duration}
                </span>
              </div>

              <h1 className="mt-3 font-heading text-2xl font-black text-background-50 md:text-4xl">
                {media.title}
              </h1>

              <div className="mt-3 flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-background-200">
                <span className="flex items-center gap-1">
                  <i className="ri-star-fill text-accent-400" aria-hidden="true" />
                  <span className="font-bold text-accent-400">{media.rating.toFixed(1)}</span>
                  <span className="text-background-300">评分</span>
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-fire-line" aria-hidden="true" />
                  热度 {formatHeat(media.heat)}
                </span>
                <span>{media.year} 年</span>
                <span>{media.region}</span>
              </div>

              <div className="mt-4 flex flex-wrap gap-2">
                {media.genres.map((genre) => (
                  <span
                    key={genre}
                    className="rounded-full border border-background-50/25 px-3 py-1 text-xs text-background-100"
                  >
                    {genre}
                  </span>
                ))}
              </div>

              <dl className="mt-5 grid grid-cols-1 gap-y-2 text-sm sm:grid-cols-2">
                <div className="flex gap-2">
                  <dt className="shrink-0 text-background-300">导演</dt>
                  <dd className="text-background-100">{media.director}</dd>
                </div>
                <div className="flex gap-2">
                  <dt className="shrink-0 text-background-300">主演</dt>
                  <dd className="text-background-100">{media.cast.join("、")}</dd>
                </div>
              </dl>

              <div className="mt-6 flex flex-wrap items-center gap-3">
                <Link
                  to={`/play/${media.id}`}
                  className="flex items-center gap-2 whitespace-nowrap rounded-md bg-accent-500 px-6 py-3 text-sm font-bold text-background-50 transition-colors hover:bg-accent-600"
                >
                  <i className="ri-play-fill text-lg" aria-hidden="true" />
                  立即播放
                </Link>
                <button
                  type="button"
                  onClick={handleFavorite}
                  className={`flex items-center gap-2 whitespace-nowrap rounded-md border px-5 py-3 text-sm font-medium transition-colors ${
                    favorited
                      ? "border-accent-400 bg-accent-500/15 text-accent-300"
                      : "border-background-50/30 text-background-100 hover:bg-background-50/10"
                  }`}
                >
                  <i
                    className={favorited ? "ri-heart-3-fill text-lg" : "ri-heart-3-line text-lg"}
                    aria-hidden="true"
                  />
                  {favorited ? "已收藏" : "收藏"}
                </button>
                <button
                  type="button"
                  onClick={handleShare}
                  className="flex items-center gap-2 whitespace-nowrap rounded-md border border-background-50/30 px-5 py-3 text-sm font-medium text-background-100 transition-colors hover:bg-background-50/10"
                >
                  <i className="ri-share-forward-line text-lg" aria-hidden="true" />
                  分享
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Body */}
      <div className="mx-auto w-full max-w-7xl px-4 py-10 md:px-6 md:py-12">
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-3">
          <div className="lg:col-span-2">
            <section>
              <h2 className="font-heading text-xl font-bold text-foreground-950">
                剧情简介
              </h2>
              <p className="mt-3 text-sm leading-relaxed text-foreground-600">
                {media.synopsis}
              </p>
              <p className="mt-3 text-sm leading-relaxed text-foreground-600">
                《{media.title}》由 {media.director} 执导，{media.cast.join("、")} 领衔主演，
                于 {media.year} 年在{media.region}地区上映。影片融合了{media.genres.join("、")}
                等元素，凭借细腻的叙事与出色的表演收获了观众的广泛好评。本篇详细剧情介绍包含剧透内容，建议观看后再行阅读。
              </p>
            </section>

            <section className="mt-8">
              <h2 className="font-heading text-xl font-bold text-foreground-950">
                相关资讯与影评
              </h2>
              {relatedNews.length > 0 ? (
                <div className="mt-4 space-y-3">
                  {relatedNews.map((news) => (
                    <Link
                      key={news.id}
                      to={`/news/${news.id}`}
                      className="group flex items-start gap-3 rounded-lg border border-background-200 bg-background-50 p-4 transition-colors hover:border-primary-200"
                    >
                      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-secondary-100 text-secondary-700">
                        <i className="ri-article-line" aria-hidden="true" />
                      </span>
                      <div className="min-w-0">
                        <h3 className="line-clamp-2 text-sm font-medium text-foreground-900 transition-colors group-hover:text-primary-600">
                          {news.title}
                        </h3>
                        <p className="mt-1 text-xs text-foreground-400">
                          {news.category} · {news.date}
                        </p>
                      </div>
                    </Link>
                  ))}
                </div>
              ) : (
                <p className="mt-3 rounded-lg border border-background-200 bg-background-50 p-4 text-sm text-foreground-500">
                  暂无关联资讯，敬请期待。
                </p>
              )}
            </section>
          </div>

          <aside className="lg:col-span-1">
            <div className="rounded-lg border border-background-200 bg-background-100 p-5">
              <h2 className="font-heading text-base font-bold text-foreground-950">
                作品资料
              </h2>
              <dl className="mt-4 space-y-3 text-sm">
                {[
                  { label: "作品名称", value: media.title },
                  { label: "类型", value: typeLabels[media.type] },
                  { label: "地区", value: media.region },
                  { label: "上映年份", value: `${media.year}` },
                  { label: "分类", value: media.genres.join(" / ") },
                  { label: "导演", value: media.director },
                  { label: "主演", value: media.cast.join("、") },
                  { label: "片长", value: media.duration },
                ].map((row) => (
                  <div key={row.label} className="flex gap-3">
                    <dt className="w-20 shrink-0 text-foreground-400">{row.label}</dt>
                    <dd className="flex-1 text-foreground-700">{row.value}</dd>
                  </div>
                ))}
              </dl>
              <div className="mt-4 flex flex-wrap gap-2 border-t border-background-200 pt-4">
                {media.tags.map((tag) => (
                  <span
                    key={tag}
                    className="rounded-full bg-accent-100 px-3 py-1 text-xs font-medium text-accent-800"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          </aside>
        </div>

        <section className="mt-12">
          <SectionHeading
            icon="ri-movie-line"
            title="相关推荐"
            subtitle="根据类型与地区为你推荐"
          />
          {related.length > 0 ? (
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:gap-4 lg:grid-cols-6">
              {related.map((item) => (
                <MovieCard key={item.id} item={item} />
              ))}
            </div>
          ) : (
            <EmptyState title="暂无相关内容，敬请期待" />
          )}
        </section>
      </div>

      {toast && (
        <div className="fixed bottom-6 left-1/2 z-[70] -translate-x-1/2 animate-fade-up">
          <div className="flex items-center gap-2 rounded-md bg-foreground-950 px-4 py-3 text-sm font-medium text-background-50">
            <i className="ri-checkbox-circle-line text-accent-400" aria-hidden="true" />
            {toast}
          </div>
        </div>
      )}

    </div>
  );
}